import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogContentText,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Box,
  Grid,
  Divider,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PaidIcon from '@mui/icons-material/PaidOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import { formatCurrency } from '../../utils/currencyFormatter';
import { invoiceDesignTokens } from '../../utils/invoiceDesignTokens';
import OrderBasedInvoiceLineItemsTable from './OrderBasedInvoiceLineItemsTable';
import CustomInvoiceLineItemsTable from './CustomInvoiceLineItemsTable';
import InvoiceEmailStatusChip from './InvoiceEmailStatusChip';
import InvoiceActionMenu from './InvoiceActionMenu';
import { getInvoiceActionState } from './invoiceActionState';

const statusColor = (status) => {
  switch (status) {
    case 'processing_issue':
    case 'processing_payment': return 'warning';
    default: return 'default';
  }
};

// "toLocaleString without seconds; drop the year when current"
// (design_handoff_invoice_detail/README.md — lifecycle strip).
const formatStepTimestamp = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const isCurrentYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(language, {
    month: 'short', day: 'numeric', ...(isCurrentYear ? {} : { year: 'numeric' }),
    hour: 'numeric', minute: '2-digit'
  });
};

const formatShortDate = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(language, { month: 'short', day: 'numeric' });
};

const LIFECYCLE_STEPS = [
  { key: 'created', label: 'Created', icon: AddCircleOutlineIcon, color: 'text.disabled', getDate: (inv) => inv.createdAt },
  { key: 'issued', label: 'Issued', icon: TaskAltIcon, color: 'primary.main', getDate: (inv) => inv.issuedAt },
  { key: 'emailed', label: 'Emailed', icon: MarkEmailReadIcon, color: 'success.main', getDate: (inv) => inv.emailSentAt },
  { key: 'paid', label: 'Paid', icon: PaidIcon, color: 'success.main', getDate: (inv) => inv.paidAt },
];

// Reached steps show a real timestamp; exactly one more (the next unreached
// step) renders dimmed after them, and anything further out isn't shown yet.
const buildLifecycleSteps = (invoice) => {
  const steps = LIFECYCLE_STEPS.map((step) => ({ ...step, date: step.getDate(invoice) }));
  let lastReachedIndex = -1;
  steps.forEach((step, index) => { if (step.date) lastReachedIndex = index; });
  return steps.slice(0, Math.min(lastReachedIndex + 2, steps.length))
    .map((step) => ({ ...step, reached: Boolean(step.date) }));
};

// Compact two-line-per-item list replacing the full desktop table on mobile
// (design_handoff_invoice_detail/README.md §Mobile).
const MobileLineItems = ({ t, i18n, invoice }) => {
  const currency = invoice.currency;
  if (invoice.invoiceType === 'order_based') {
    return (
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        {(invoice.lineItems || []).map((li, orderIndex) => {
          const orderKey = li.sourceOrderId || li.sourceOrderNumber || orderIndex;
          const orderLabel = li.description || li.sourceOrderNumber || '';
          const orderDate = li.orderDate ? new Date(li.orderDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }) : '';
          return (
            <Box key={orderKey} sx={{ borderTop: orderIndex > 0 ? '1px solid' : 0, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {orderLabel} {orderDate && <Typography component="span" variant="caption" color="text.secondary">{orderDate}</Typography>}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(li.subtotal, currency, i18n.language)}</Typography>
              </Box>
              {invoice.includeItemBreakdown !== false && (li.orderItems || []).map((oi, itemIndex) => (
                <Box key={oi.productId || itemIndex} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, pl: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="body2">
                      {oi.productName} <Typography component="span" variant="caption" color="text.secondary">× {oi.quantity}</Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {formatCurrency(oi.unitPrice, currency, i18n.language)}
                      {Number(oi.discount) ? ` · -${formatCurrency(oi.discount, currency, i18n.language)}` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{formatCurrency(oi.subtotal, currency, i18n.language)}</Typography>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
      {(invoice.lineItems || []).map((li, index) => (
        <Box key={li.productId ? `${li.productId}-${index}` : index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderTop: index > 0 ? '1px solid' : 0, borderColor: 'divider' }}>
          <Box>
            <Typography variant="body2">
              {li.productName || li.description} <Typography component="span" variant="caption" color="text.secondary">× {li.quantity}</Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {formatCurrency(li.unitPrice, currency, i18n.language)}
              {Number(li.discount) ? ` · -${formatCurrency(li.discount, currency, i18n.language)}` : ''}
            </Typography>
          </Box>
          <Typography variant="body2">{formatCurrency(li.subtotal, currency, i18n.language)}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const InvoiceDetailDialog = ({
  open,
  onClose,
  invoice,
  onEdit,
  onRevise = () => {},
  onRemind = () => {},
  onSendEmail = () => {},
  onIssue,
  onDelete,
  onMarkPaid,
  onVoid,
  onDownloadPdf,
  onPrintShippingLabel = () => {},
  onDownloadShippingLabel = () => {},
  onRefresh = () => {},
  processingIssueNeedsAttention = false
}) => {
  const { t, i18n } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Same breakpoint InvoiceRow stacks at, kept as a literal query so specs
  // work without a ThemeProvider.
  const isMobile = useMediaQuery('(max-width:899.95px)');
  const isProcessingPayment = invoice && invoice.status === 'processing_payment';
  const isProcessingIssue = invoice && invoice.status === 'processing_issue';
  const isProcessing = isProcessingPayment || isProcessingIssue;
  const {
    isDraft, isPaid, isVoid,
    isIssuedNotSent, isIssuedSent, canDownloadPdf, canUseShippingLabel,
  } = invoice ? getInvoiceActionState(invoice) : {};
  const renderedStatus = invoice?.status === 'processing_issue'
    ? (processingIssueNeedsAttention ? t('Needs manual review') : t('Issuing…'))
    : invoice?.status === 'processing_payment'
      ? t('Processing payment')
      : invoice ? t(invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)) : '';

  const closeMenu = () => setMenuAnchor(null);
  const closeDrawer = () => setDrawerOpen(false);

  // Per-line/per-order-item discounts are already netted into invoice.subtotal server-side,
  // for both invoice types. Show Gross amount as the pre-discount figure instead, with item
  // discounts broken out on their own line — otherwise it looks pre-discounted while implying
  // a further deduction, which reads as the discount being applied twice. Custom invoices carry
  // `discount` directly on each line item; order-based invoices carry it on each nested
  // `orderItems` entry instead (top-level order rows never discount).
  const itemDiscountsTotal = (invoice?.lineItems || []).reduce((sum, li) => (
    sum + (li.orderItems && li.orderItems.length > 0
      ? li.orderItems.reduce((oiSum, oi) => oiSum + (Number(oi.discount) || 0), 0)
      : (Number(li.discount) || 0))
  ), 0);
  const showItemDiscounts = itemDiscountsTotal > 0;
  const grossSubtotal = (Number(invoice?.subtotal) || 0) + itemDiscountsTotal;

  // Billing block snapshotted onto the recipient at creation (user recipients
  // only) — rendered QuickBooks-style: company, street, "City Province
  // Postal", country.
  const billing = invoice?.recipient?.billingAddress || null;
  const billingLocality = billing
    ? [billing.city, billing.state, billing.postalCode].filter(Boolean).join(' ')
    : '';
  const billingLines = billing
    ? [billing.companyName, billing.addressLine1, billing.addressLine2, billingLocality, billing.country].filter(Boolean)
    : [];

  // "Emailed to" meta field — one line per recipient (address, cc tag, status
  // chip, no Email:/CC: prefix since the address itself is shown).
  const emailedToLines = invoice?.emailRecipients?.length > 0
    ? invoice.emailRecipients.map((r) => ({ email: r.email, isCc: r.type === 'cc', status: r.status }))
    : (invoice?.emailSentAt && (invoice?.emailSentTo || invoice?.recipient?.email)
      ? [{ email: invoice.emailSentTo || invoice.recipient.email, isCc: false, status: invoice.emailStatus }]
      : []);

  // -- Header primary/quick action: same state table as the row --
  // (design_handoff_invoice_detail/README.md).
  const emailInvoiceLabel = t('Email Invoice', { defaultValue: 'Email Invoice' });
  let headerPrimary = null;
  let headerQuick = null;
  if (invoice && isProcessing) {
    headerPrimary = { type: 'refresh' };
  } else if (isDraft) {
    headerPrimary = { type: 'button', label: t('Issue'), icon: <SendIcon fontSize="small" />, onClick: () => onIssue(invoice) };
    headerQuick = { label: t('Edit'), icon: <EditIcon fontSize="small" />, onClick: () => onEdit(invoice) };
  } else if (isIssuedNotSent) {
    headerPrimary = { type: 'button', label: emailInvoiceLabel, icon: <MailOutlineIcon fontSize="small" />, onClick: () => onSendEmail(invoice) };
    headerQuick = { label: t('Mark paid'), icon: <PaidIcon fontSize="small" />, onClick: () => onMarkPaid(invoice), color: 'success' };
  } else if (isIssuedSent) {
    headerPrimary = { type: 'button', label: t('Mark paid'), icon: <PaidIcon fontSize="small" />, onClick: () => onMarkPaid(invoice), color: 'success' };
    headerQuick = { label: t('Send reminder', { defaultValue: 'Send reminder' }), icon: <NotificationsNoneIcon fontSize="small" />, onClick: () => onRemind(invoice), color: 'primary' };
  } else if (isPaid) {
    headerPrimary = { type: 'caption', text: `${t('Paid')} ${invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString(i18n.language) : ''}` };
    headerQuick = { label: emailInvoiceLabel, icon: <MailOutlineIcon fontSize="small" />, onClick: () => onSendEmail(invoice) };
  } else if (isVoid) {
    headerPrimary = { type: 'caption', text: `${t('Voided')} ${invoice.updatedAt ? new Date(invoice.updatedAt).toLocaleDateString(i18n.language) : ''}` };
  }

  const reviseExtraItem = invoice && invoice.invoiceType === 'custom' && invoice.status === 'issued' ? (
    <MenuItem onClick={() => { onRevise(invoice); closeMenu(); closeDrawer(); }}>
      <ListItemIcon><EditNoteIcon fontSize="small" /></ListItemIcon>
      <ListItemText>{t('Revise', { defaultValue: 'Revise' })}</ListItemText>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>{t('custom only', { defaultValue: 'custom only' })}</Typography>
    </MenuItem>
  ) : null;

  const lifecycleSteps = invoice && !isProcessing ? buildLifecycleSteps(invoice) : [];

  const mobileMetaLine = invoice ? [
    invoice.invoiceType === 'custom' ? t('Custom') : t('Order based'),
    invoice.dueDate ? `${t('due', { defaultValue: 'due' })} ${new Date(invoice.dueDate).toLocaleDateString(i18n.language)}` : null,
  ].filter(Boolean).join(' · ') : '';

  if (!invoice) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={!isMobile} fullScreen={isMobile}>
      {/* Header cluster replaces the old DialogTitle + DialogActions footer:
          [quick action] [primary] [Packing slip] [PDF] [⋯] | [×]
          (design_handoff_invoice_detail/README.md §Header). */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, rowGap: 1, flexWrap: isMobile ? 'nowrap' : 'wrap',
        px: isMobile ? 2 : 3.5, py: isMobile ? 1.5 : 2, borderBottom: '1px solid', borderColor: 'divider',
      }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, rowGap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="span" noWrap={isMobile}>
              {invoice.invoiceNumber || t('Draft')}
            </Typography>
            <Chip
              size="small"
              color={invoiceDesignTokens.statusPill[invoice.status] ? undefined : statusColor(invoice.status)}
              sx={invoiceDesignTokens.statusPill[invoice.status]
                ? {
                    backgroundColor: invoiceDesignTokens.statusPill[invoice.status].bg,
                    color: invoiceDesignTokens.statusPill[invoice.status].color
                  }
                : undefined}
              label={renderedStatus}
            />
            {(invoice.revision || 0) > 0 && !isMobile && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={t('Revised (Rev. {{n}})', { n: invoice.revision, defaultValue: `Revised (Rev. ${invoice.revision})` })}
              />
            )}
          </Box>
          {isMobile && mobileMetaLine && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {mobileMetaLine}
            </Typography>
          )}
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {headerPrimary?.type === 'refresh' && (
              <Button size="small" onClick={() => onRefresh(invoice._id)}>{t('Refresh')}</Button>
            )}
            {headerQuick && (
              <Button
                size="small"
                variant="outlined"
                color={headerQuick.color || 'primary'}
                startIcon={headerQuick.icon}
                onClick={headerQuick.onClick}
                sx={!headerQuick.color ? { color: 'text.secondary', borderColor: 'divider' } : undefined}
              >
                {headerQuick.label}
              </Button>
            )}
            {headerPrimary?.type === 'button' && (
              <Button size="small" variant="contained" color={headerPrimary.color} startIcon={headerPrimary.icon} onClick={headerPrimary.onClick}>
                {headerPrimary.label}
              </Button>
            )}
            {headerPrimary?.type === 'caption' && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, minWidth: 96, textAlign: 'center' }}>
                {headerPrimary.text}
              </Typography>
            )}
            {canUseShippingLabel && (
              <Tooltip title={t('Packing slip')}>
                <IconButton size="small" onClick={() => onPrintShippingLabel(invoice)} aria-label={t('Packing slip')}>
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDownloadPdf && (
              <Tooltip title={t('Download PDF')}>
                <IconButton size="small" onClick={() => onDownloadPdf(invoice)} aria-label={t('Download PDF')}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isProcessing && (
              <>
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={t('Actions')}>
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                <InvoiceActionMenu
                  t={t}
                  invoice={invoice}
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={closeMenu}
                  onSendEmail={onSendEmail}
                  onRemind={onRemind}
                  onMarkPaid={onMarkPaid}
                  onDownloadPdf={onDownloadPdf}
                  onPrintLabel={onPrintShippingLabel}
                  onDownloadLabel={onDownloadShippingLabel}
                  onEdit={onEdit}
                  onVoid={onVoid}
                  onDelete={onDelete}
                  extraItems={reviseExtraItem}
                />
              </>
            )}
            <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center', mx: 0.5 }} />
          </Box>
        )}

        <IconButton
          onClick={onClose}
          aria-label={t('Close', { defaultValue: 'Close' })}
          sx={isMobile ? { width: 44, height: 44 } : undefined}
        >
          <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
        </IconButton>
      </Box>

      {lifecycleSteps.length > 0 && (
        <Box
          data-testid="lifecycle-strip"
          sx={{
            display: 'flex', flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center',
            px: isMobile ? 2 : 3.5, py: 1.25, bgcolor: 'background.default',
            borderBottom: '1px solid', borderColor: 'divider', fontSize: 12,
          }}
        >
          {lifecycleSteps.map((step, index) => (
            <React.Fragment key={step.key}>
              {index > 0 && <ChevronRightIcon sx={{ fontSize: 16, color: 'divider', mx: 1.5 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, opacity: step.reached ? 1 : 0.55 }}>
                {step.reached
                  ? <step.icon sx={{ fontSize: 15, color: step.color }} />
                  : <RadioButtonUncheckedIcon sx={{ fontSize: 15 }} />}
                <Typography variant="caption" color="text.secondary">{t(step.label)}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {step.reached
                    ? formatStepTimestamp(step.date, i18n.language)
                    : (step.key === 'paid' && invoice.dueDate
                      ? `${t('due', { defaultValue: 'due' })} ${formatShortDate(invoice.dueDate, i18n.language)}`
                      : '')}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
          {invoice.reminderSentAt && (
            <React.Fragment key="reminder">
              <ChevronRightIcon sx={{ fontSize: 16, color: 'divider', mx: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <NotificationsNoneIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">{t('Reminder sent', { defaultValue: 'Reminder sent' })}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatStepTimestamp(invoice.reminderSentAt, i18n.language)}</Typography>
              </Box>
            </React.Fragment>
          )}
          {(invoice.revision || 0) > 0 && invoice.revisedAt && (
            <React.Fragment key="revised">
              <ChevronRightIcon sx={{ fontSize: 16, color: 'divider', mx: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <HistoryIcon sx={{ fontSize: 15, color: 'warning.main' }} />
                <Typography variant="caption" color="warning.main">
                  {t('Revised', { defaultValue: 'Revised' })} ({t('Rev. {{n}}', { n: invoice.revision, defaultValue: `Rev. ${invoice.revision}` })})
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatStepTimestamp(invoice.revisedAt, i18n.language)}</Typography>
              </Box>
            </React.Fragment>
          )}
        </Box>
      )}

      <DialogContent sx={{ pb: isMobile && !isProcessing ? 14 : undefined }}>
        {isProcessingIssue ? (
          <DialogContentText color={processingIssueNeedsAttention ? 'error' : 'text.secondary'}>
            {processingIssueNeedsAttention
              ? t('This invoice is stuck while issuing and needs manual review. Do not retry, edit, delete, or void it. Backend support must resolve its status.')
              : t('This invoice is being issued. Wait a moment and refresh to check its latest status.')}
          </DialogContentText>
        ) : isProcessingPayment ? (
          <DialogContentText>
            {t('This invoice is processing payment. Refresh to check its latest status.')}
          </DialogContentText>
        ) : (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">{t('Bill to', { defaultValue: 'Bill to' })}</Typography>
                <Typography variant="body1">{invoice.recipient?.name || ''}</Typography>
                {billingLines.map((line) => (
                  <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
                ))}
                {invoice.recipient?.email && (
                  <Typography variant="body2" color="text.secondary">{invoice.recipient.email}</Typography>
                )}
                {invoice.recipient?.phone && (
                  <Typography variant="body2" color="text.secondary">{invoice.recipient.phone}</Typography>
                )}
                {invoice.recipient?.address && (
                  <Typography variant="body2" color="text.secondary">{invoice.recipient.address}</Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">{t('Invoice type')}</Typography>
                <Typography variant="body1">
                  {invoice.invoiceType === 'custom' ? t('Custom') : t('Order based')}
                </Typography>
                {invoice.invoiceType === 'order_based' && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('Period')}</Typography>
                    <Typography variant="body1">
                      {invoice.periodStart ? new Date(invoice.periodStart).toLocaleDateString() : ''} - {invoice.periodEnd ? new Date(invoice.periodEnd).toLocaleDateString() : ''}
                    </Typography>
                  </>
                )}
                {invoice.dueDate && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('Due date', { defaultValue: 'Due date' })}</Typography>
                    <Typography variant="body1">{new Date(invoice.dueDate).toLocaleDateString()}</Typography>
                  </>
                )}
                {emailedToLines.length > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('Emailed to', { defaultValue: 'Emailed to' })}</Typography>
                    {emailedToLines.map((line) => (
                      <Box key={line.email} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body1">
                          {line.email}
                          {line.isCc && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              {t('cc', { defaultValue: 'cc' })}
                            </Typography>
                          )}
                        </Typography>
                        <InvoiceEmailStatusChip t={t} emailStatus={line.status} emailSentAt={invoice.emailSentAt} />
                      </Box>
                    ))}
                  </>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {isMobile ? (
              <Box sx={{ mb: 2, fontSize: 13 }}>
                <MobileLineItems t={t} i18n={i18n} invoice={invoice} />
              </Box>
            ) : (
              /* The line-item tables are wider than a phone, so they scroll on
                 their own instead of dragging the whole dialog body sideways. */
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                {invoice.invoiceType === 'order_based' ? (
                  <OrderBasedInvoiceLineItemsTable
                    lineItems={invoice.lineItems || []}
                    currency={invoice.currency}
                    includeItemBreakdown={invoice.includeItemBreakdown !== false}
                  />
                ) : (
                  <CustomInvoiceLineItemsTable
                    lineItems={invoice.lineItems || []}
                    currency={invoice.currency}
                    serviceDate={invoice.issuedAt || invoice.createdAt}
                  />
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ minWidth: 260, width: isMobile ? '100%' : undefined }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">{t('Gross amount')}</Typography>
                  <Typography color="text.secondary">{formatCurrency(grossSubtotal, invoice.currency, i18n.language)}</Typography>
                </Box>
                {showItemDiscounts && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">{t('Item discounts')}</Typography>
                    <Typography color="text.secondary">-{formatCurrency(itemDiscountsTotal, invoice.currency, i18n.language)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">{t('Order discount')}</Typography>
                  <Typography color="text.secondary">-{formatCurrency(invoice.discountTotal, invoice.currency, i18n.language)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">{t('Tax')} ({invoice.taxRate}%)</Typography>
                  <Typography color="text.secondary">{formatCurrency(invoice.taxAmount, invoice.currency, i18n.language)}</Typography>
                </Box>
                <Box sx={{ borderTop: '1.5px solid', borderColor: 'divider', mt: 0.5, pt: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: `${invoiceDesignTokens.fontSize.metaValue}px` }} color="text.primary">{t('Total')}</Typography>
                    <Typography sx={{ fontSize: `${invoiceDesignTokens.fontSize.grandTotal}px` }} color="primary.main">{formatCurrency(invoice.total, invoice.currency, i18n.language)}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {invoice.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">{t('Notes')}</Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>{invoice.notes}</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {isMobile && !isProcessing && (
        <Box sx={{
          borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper',
          boxShadow: '0 -8px 24px rgba(42,30,51,0.06)',
        }}
        >
          {headerPrimary?.type === 'button' && (
            <Button
              variant="contained" color={headerPrimary.color} startIcon={headerPrimary.icon} onClick={headerPrimary.onClick}
              sx={{ flex: 1, height: 46, borderRadius: 999 }}
            >
              {headerPrimary.label}
            </Button>
          )}
          {headerPrimary?.type === 'caption' && (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textAlign: 'center' }}>
              {headerPrimary.text}
            </Typography>
          )}
          {headerQuick && (
            <Tooltip title={headerQuick.label}>
              <IconButton
                onClick={headerQuick.onClick}
                aria-label={headerQuick.label}
                sx={{
                  width: 46, height: 46, border: '1px solid', borderColor: 'divider',
                  color: headerQuick.color ? `${headerQuick.color}.main` : 'text.secondary',
                }}
              >
                {headerQuick.icon}
              </IconButton>
            </Tooltip>
          )}
          {canUseShippingLabel && (
            <Tooltip title={t('Packing slip')}>
              <IconButton onClick={() => onPrintShippingLabel(invoice)} aria-label={t('Packing slip')} sx={{ width: 46, height: 46, border: '1px solid', borderColor: 'divider' }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDownloadPdf && (
            <Tooltip title={t('Download PDF')}>
              <IconButton onClick={() => onDownloadPdf(invoice)} aria-label={t('Download PDF')} sx={{ width: 46, height: 46, border: '1px solid', borderColor: 'divider' }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={() => setDrawerOpen(true)} aria-label={t('Actions')} sx={{ width: 46, height: 46, border: '1px solid', borderColor: 'divider' }}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {isMobile && (
        <InvoiceActionMenu
          t={t}
          invoice={invoice}
          variant="drawer"
          open={drawerOpen}
          onClose={closeDrawer}
          onSendEmail={onSendEmail}
          onRemind={onRemind}
          onMarkPaid={onMarkPaid}
          onDownloadPdf={onDownloadPdf}
          onPrintLabel={onPrintShippingLabel}
          onDownloadLabel={onDownloadShippingLabel}
          onEdit={onEdit}
          onVoid={onVoid}
          onDelete={onDelete}
          extraItems={reviseExtraItem}
        />
      )}
    </Dialog>
  );
};

export default InvoiceDetailDialog;
